import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShieldCheck, Search, Menu, X, CalendarCheck, User2, LogOut, LayoutGrid, ChevronDown, Store, MessageCircle, Bell, Heart, Ticket, Wallet, Plane, ShoppingBag } from "lucide-react";
import { Logo } from "@/components/Logo";

// Same source as the HQ router (pages/admin/AdminApp ADMIN_PATH); HQ itself 404s for anyone who isn't staff.
const HQ_PATH = (process.env.REACT_APP_ADMIN_PATH || "/hq-9vq4tz").replace(/\/+$/, "");
import { WORLDS } from "@/lib/worlds";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Stage — the social half of the platform — gets its own section in the nav, level with the worlds.
export const STAGE_LINKS = [
  { to: "/stage/feed", label: "Pulse feed" },
  { to: "/stage/reels", label: "Reels" },
  { to: "/stage/live", label: "Live" },
  { to: "/creators", label: "Creators" },
  { to: "/circles", label: "Circles" },
  { to: "/collabs", label: "Collab" },
  { to: "/stage/news", label: "News" },
  { to: "/stage/sports", label: "Sports" },
  { to: "/leaderboards/creators", label: "Top creators" },
  { to: "/stage", label: "About Stage" },
];

// Fable Ops — the separate operations platform for multi-site businesses (website only; app coming soon).
export const OPS_LINKS = [
  { to: "/ops", label: "Fable Ops overview" },
  { to: "/ops/products/rotas", label: "Rotas & shifts" },
  { to: "/ops/products/hr", label: "HR" },
  { to: "/ops/products/pay", label: "Pay" },
  { to: "/ops/products/comms", label: "Team chat" },
  { to: "/ops/products/boards", label: "Boards & checklists" },
  { to: "/ops/pricing", label: "Pricing" },
  { to: "/ops/app", label: "Open Fable Ops" },
];

// Information pages from the old website, kept one click away.
export const SITE_LINKS = [
  { to: "/worlds", label: "All worlds" },
  { to: "/yasmin", label: "Yasmin" },
  { to: "/travel/trips", label: "My trips" },
  { to: "/events", label: "Events" },
  { to: "/shop", label: "Shop" },
  { to: "/business", label: "For business" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About" },
];

export function Navbar() {
  const { user, bootstrapped, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/marketplace?q=${encodeURIComponent(q)}`);
    setMobileOpen(false);
  };

  const initials = (user?.full_name || user?.email || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <header className="sticky top-0 z-40 border-b border-border glass">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6">
        <Logo className="text-2xl" />

        <nav className="ml-4 hidden items-center gap-1 lg:flex">
          {WORLDS.map((w) => (
            <Link
              key={w.key}
              to={`/marketplace?world=${w.key}`}
              data-testid={`nav-world-${w.key}`}
              className="rounded-full px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              {w.name}
            </Link>
          ))}
          <Link
            to="/travel"
            data-testid="nav-travel"
            className={"rounded-full px-3 py-1.5 text-sm font-medium transition-colors hover:bg-muted hover:text-foreground " + (location.pathname.startsWith("/travel") ? "bg-muted text-foreground" : "text-muted-foreground")}
          >
            Travel
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="nav-stage"
                className={`ml-1 inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${location.pathname.startsWith("/stage") || location.pathname.startsWith("/creators") || location.pathname.startsWith("/@") || location.pathname.startsWith("/circles") || location.pathname.startsWith("/collabs") ? "text-white" : "hover:bg-[var(--fable-soft)]"}`}
                style={location.pathname.startsWith("/stage") || location.pathname.startsWith("/creators") || location.pathname.startsWith("/@") || location.pathname.startsWith("/circles") || location.pathname.startsWith("/collabs") ? { background: "var(--fable)" } : { color: "var(--fable)" }}
              >
                Stage <ChevronDown size={14} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              {STAGE_LINKS.map((l) => (
                <DropdownMenuItem key={l.to} onClick={() => navigate(l.to)} data-testid={`nav-stage-${l.to.slice(1).replace(/\//g, "-")}`}>
                  {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="nav-more"
                className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                More <ChevronDown size={14} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              {SITE_LINKS.map((l) => (
                <DropdownMenuItem key={l.to} onClick={() => navigate(l.to)} data-testid={`nav-more-${l.to.slice(1)}`}>
                  {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-ops"
                  className="inline-flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-semibold text-sky-700 transition-colors hover:bg-sky-50 dark:text-sky-300 dark:hover:bg-sky-950/40"
                >
                  Fable Ops <ChevronDown size={14} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {OPS_LINKS.map((l) => (
                  <DropdownMenuItem key={l.to} onClick={() => navigate(l.to)} data-testid={"nav-ops-" + (l.to.split("/").pop() || "home")}>
                    {l.label}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5 text-xs text-muted-foreground">Fable Ops app: coming soon</div>
              </DropdownMenuContent>
            </DropdownMenu>
        </nav>

        <form onSubmit={submitSearch} className="ml-auto hidden flex-1 max-w-xs items-center md:flex">
          <div className="relative w-full">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              data-testid="nav-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search Fable +"
              className="w-full rounded-full border border-border bg-background py-2 pl-9 pr-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>
        </form>

        <div className="ml-auto flex items-center gap-2 md:ml-2">
          {!bootstrapped ? (
            <div className="h-9 w-20 animate-pulse rounded-full bg-muted" />
          ) : user ? (
            <>
              <Link to="/bookings" className="hidden sm:inline-flex">
                <Button variant="ghost" size="sm" data-testid="nav-bookings" className="gap-1.5">
                  <CalendarCheck size={16} /> Bookings
                </Button>
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="nav-profile-menu"
                    className="flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold text-white"
                    style={{ background: "var(--fable)" }}
                  >
                    {user.avatar ? (
                      <img src={user.avatar} alt="" className="h-9 w-9 rounded-full object-cover" />
                    ) : (
                      initials
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <div className="px-2 py-1.5">
                    <p className="truncate text-sm font-semibold">{user.full_name}</p>
                    <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                    <User2 size={15} className="mr-2" /> Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/bookings")} data-testid="menu-bookings">
                    <CalendarCheck size={15} className="mr-2" /> My bookings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/messages")} data-testid="menu-messages">
                    <MessageCircle size={15} className="mr-2" /> Messages
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/notifications")} data-testid="menu-notifications">
                    <Bell size={15} className="mr-2" /> Notifications
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/favourites")} data-testid="menu-favourites">
                    <Heart size={15} className="mr-2" /> Favourites
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/tickets")} data-testid="menu-tickets">
                    <Ticket size={15} className="mr-2" /> My tickets
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/shop/orders")} data-testid="menu-orders">
                    <ShoppingBag size={15} className="mr-2" /> My orders
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/travel/trips")} data-testid="menu-trips">
                    <Plane size={15} className="mr-2" /> My trips
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/wallet")} data-testid="menu-wallet">
                    <Wallet size={15} className="mr-2" /> Wallet
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/marketplace")} data-testid="menu-marketplace">
                    <LayoutGrid size={15} className="mr-2" /> Explore
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/partner")} data-testid="menu-partner">
                    <Store size={15} className="mr-2" /> {user.is_partner ? "Partner hub" : "Sell on Fable +"}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {(user.is_admin || (user.admin_scopes && Object.keys(user.admin_scopes).length > 0)) && (
                    <DropdownMenuItem onClick={() => navigate(HQ_PATH)} data-testid="menu-fable-hq" className="font-medium text-[#E0007A] focus:text-[#E0007A]">
                      <ShieldCheck className="mr-2 h-4 w-4" /> Open Fable HQ
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-destructive focus:text-destructive">
                    <LogOut size={15} className="mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/login" state={{ from: location.pathname }} className="hidden sm:inline-flex">
                <Button variant="ghost" size="sm" data-testid="nav-login">
                  Sign in
                </Button>
              </Link>
              <Link to="/register">
                <Button size="sm" data-testid="nav-register" className="rounded-full">
                  Get started
                </Button>
              </Link>
            </>
          )}
          <button
            className="inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-muted lg:hidden"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-border bg-background px-4 pb-4 pt-3 lg:hidden">
          <form onSubmit={submitSearch} className="mb-3 md:hidden">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search Fable +"
                className="w-full rounded-full border border-border bg-background py-2 pl-9 pr-3 text-sm outline-none"
              />
            </div>
          </form>
          <div className="grid grid-cols-2 gap-2">
            {WORLDS.map((w) => {
              const Icon = w.icon;
              return (
                <Link
                  key={w.key}
                  to={`/marketplace?world=${w.key}`}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 rounded-xl border border-border px-3 py-2.5 text-sm font-medium"
                >
                  <Icon size={16} style={{ color: w.accent }} /> {w.name}
                </Link>
              );
            })}
          </div>
          <Link to="/travel" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-travel" className="mt-3 flex items-center justify-between rounded-xl px-3 py-3 text-white" style={{ background: "linear-gradient(135deg,#3A0CA3,#8200EF 55%,#FB0077)" }}>
          <span><span className="block text-sm font-bold">Travel</span><span className="block text-xs text-white/85">Trips, flights and Yasmin</span></span>
          <ChevronDown size={16} className="-rotate-90" />
          </Link>
          <div className="mt-3 rounded-xl p-3 text-white" style={{ background: "var(--fable)" }}>
            <p className="text-sm font-bold">Stage</p>
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-sm font-medium">
              {STAGE_LINKS.map((l) => (
                <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="hover:underline">{l.label}</Link>
              ))}
            </div>
          </div>
          <div className="mt-3 rounded-xl border border-sky-200 bg-sky-50 p-3 text-sky-950 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-100">
            <p className="text-sm font-bold">Fable Ops <span className="font-normal opacity-70">· for multi-site businesses</span></p>
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-sm font-medium">
              {OPS_LINKS.map((l) => (
                <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="hover:underline">{l.label}</Link>
              ))}
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 border-t border-border pt-3 text-sm font-medium">
            {SITE_LINKS.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="text-muted-foreground hover:text-foreground">
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
